import { useState } from 'react';
import type { JourneyPayload, SemanticEvent } from '../../../types/analyticstypes';
import JourneyEventRow from './JourneyEventRow';
import UpsellFunnelCard from './UpsellFunnelCard';
import AgeVerificationCard from './AgeVerificationCard';

type Band = 'navigation' | 'cart' | 'age' | 'upsell' | 'checkout' | 'payment' | 'session';

const BAND_FOR: Record<string, Band> = {
  session_start: 'session',
  session_cancel: 'session',
  session_timeout: 'session',
  order_complete: 'session',
  order_error: 'session',
  help_requested: 'session',
  language_change: 'session',
  menu_navigate: 'navigation',
  menu_back: 'navigation',
  item_view: 'navigation',
  cart_open: 'cart',
  item_add_to_cart: 'cart',
  item_remove_from_cart: 'cart',
  scan_success: 'cart',
  scan_not_found: 'cart',
  scan_do_not_sell: 'cart',
  unsellable_shown: 'cart',
  age_verify_requested: 'age',
  age_approved: 'age',
  age_auto_approved: 'age',
  age_denied: 'age',
  age_cancelled: 'age',
  upsell_panel_opened: 'upsell',
  upsell_panel_closed: 'upsell',
  upsell_impression: 'upsell',
  upsell_accept: 'upsell',
  upsell_modal_opened: 'upsell',
  upsell_modal_dismissed: 'upsell',
  upsell_filtered_invalid: 'upsell',
  add_ons_shown: 'upsell',
  add_ons_dismissed: 'upsell',
  checkout_start: 'checkout',
  review_shown: 'checkout',
  review_pay_tapped: 'checkout',
  review_back: 'checkout',
  bag_yes: 'checkout',
  bag_no: 'checkout',
  payment_initiated: 'payment',
  payment_approved: 'payment',
  payment_declined: 'payment',
  payment_cancelled: 'payment',
  payment_timeout: 'payment',
};

const BAND_LABEL: Record<Band, string> = {
  session: 'Session',
  navigation: 'Navigation',
  cart: 'Cart & Scan',
  age: 'Age Verification',
  upsell: 'Upsell / Add-ons',
  checkout: 'Checkout',
  payment: 'Payment',
};

const BAND_COLOR: Record<Band, string> = {
  session: 'text-slate-700 bg-slate-50 border-slate-200',
  navigation: 'text-blue-700 bg-blue-50 border-blue-200',
  cart: 'text-amber-700 bg-amber-50 border-amber-200',
  age: 'text-red-700 bg-red-50 border-red-200',
  upsell: 'text-fuchsia-700 bg-fuchsia-50 border-fuchsia-200',
  checkout: 'text-purple-700 bg-purple-50 border-purple-200',
  payment: 'text-green-700 bg-green-50 border-green-200',
};

const BAND_TEXT: Record<Band, string> = {
  session: 'text-slate-700',
  navigation: 'text-blue-700',
  cart: 'text-amber-700',
  age: 'text-red-700',
  upsell: 'text-fuchsia-700',
  checkout: 'text-purple-700',
  payment: 'text-green-700',
};

function bandFor(type: string): Band {
  return BAND_FOR[type] ?? 'session';
}

export default function JourneyTab({ journey }: { journey: JourneyPayload }) {
  const allEvents: SemanticEvent[] = journey.semanticEvents || [];
  const buckets: Record<Band, SemanticEvent[]> = {
    session: [],
    navigation: [],
    cart: [],
    age: [],
    upsell: [],
    checkout: [],
    payment: [],
  };
  for (const ev of allEvents) buckets[bandFor(ev.type as string)].push(ev);

  const orderedBands: Band[] = [
    'session',
    'navigation',
    'cart',
    'age',
    'upsell',
    'checkout',
    'payment',
  ];

  const [open, setOpen] = useState<Record<Band, boolean>>({
    session: true,
    navigation: true,
    cart: true,
    age: true,
    upsell: true,
    checkout: true,
    payment: true,
  });

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <UpsellFunnelCard journey={journey} />
        <AgeVerificationCard journey={journey} />
      </div>

      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <h4 className="text-sm font-semibold text-gray-800">Event Timeline</h4>
          <span className="text-[11px] text-gray-500">
            {allEvents.length} event{allEvents.length !== 1 ? 's' : ''}
          </span>
        </div>
        {orderedBands.map((band) => {
          if (buckets[band].length === 0) return null;
          const isOpen = open[band];
          return (
            <div key={band} className="border border-gray-200 rounded-lg bg-white">
              <button
                onClick={() => setOpen((o) => ({ ...o, [band]: !o[band] }))}
                className={`w-full flex items-center justify-between gap-2 px-3 py-2 text-xs font-semibold ${BAND_TEXT[band]}`}
              >
                <span className="flex items-center gap-2">
                  <svg
                    className={`w-3 h-3 transition-transform ${isOpen ? 'rotate-90' : ''}`}
                    fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                  </svg>
                  {BAND_LABEL[band]}
                </span>
                <span className={`px-1.5 py-0.5 rounded-full text-[10px] border ${BAND_COLOR[band]}`}>
                  {buckets[band].length}
                </span>
              </button>
              {isOpen && (
                <div className="border-t border-gray-100 px-1 py-1">
                  {buckets[band].map((ev, i) => (
                    <JourneyEventRow
                      key={i}
                      event={ev}
                      sessionStartTs={journey.sessionStartTs}
                      highlightColor={BAND_TEXT[band]}
                    />
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
